import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';

/**
 * Remembers an Adyen result after which money may still arrive (`Pending` / `Received`).
 *
 * The Drop-in host (`app-cw-adyen`) is rendered only while the booking form is valid, so a form
 * that goes invalid and valid again destroys it and builds a fresh one. A hold kept on that
 * instance died with it, and the fresh instance minted a second payable session while the first
 * payment could still complete - two charges for one booking. Kept here, in a root service, the
 * hold outlives every rebuild.
 *
 * A hold is scoped to ONE booking (partner + the booking it was for, see `buildAdyenHoldScope`):
 * a customer who moves on to a different slot or service is not paying for the held booking, and
 * must not inherit its hold. It is released only by the customer pressing "Try again" on the
 * Pending message - never by choosing a payment method, which after a refresh the customer has to
 * do anyway, and which would otherwise dissolve the very hold the refresh was meant to keep.
 *
 * Persisted in sessionStorage beside the parked redirect session (`cw_adyen_pending_<partner>`),
 * so a refresh keeps it - a refresh used to forget it and hand the customer a second payable
 * session. sessionStorage, not localStorage: like the parked session it must not outlive the tab.
 */
@Injectable({ providedIn: 'root' })
export class AdyenPaymentHoldService {

  /** Used when storage is unavailable (private mode), so a hold still outlives a rebuild. */
  private readonly memory = new Map<string, Record<string, string>>();

  /** Records that the payment for this booking ended in `reason` and may still settle. */
  hold(partnerId: number | undefined, scope: string, reason: string): void {
    const holds = this.read(partnerId);
    holds[scope] = reason;
    this.write(partnerId, holds);
  }

  /** The reason a payment is held for this booking, or null when a new session may be minted. */
  heldReason(partnerId: number | undefined, scope: string): string | null {
    return this.read(partnerId)[scope] ?? null;
  }

  /** The customer pressed "Try again" on this booking's held payment. */
  release(partnerId: number | undefined, scope: string): void {
    const holds = this.read(partnerId);
    delete holds[scope];
    this.write(partnerId, holds);
  }

  private storageKey(partnerId: number | undefined): string {
    return `cw_adyen_hold_${partnerId ?? 'unknown'}`;
  }

  private read(partnerId: number | undefined): Record<string, string> {
    const key = this.storageKey(partnerId);
    try {
      const raw = window.sessionStorage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? { ...(parsed as Record<string, string>) } : {};
    } catch (error) {
      LoggerService.error(error);
      return { ...(this.memory.get(key) ?? {}) };
    }
  }

  private write(partnerId: number | undefined, holds: Record<string, string>): void {
    const key = this.storageKey(partnerId);
    this.memory.set(key, holds);
    try {
      if (Object.keys(holds).length === 0) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, JSON.stringify(holds));
      }
    } catch (error) {
      // Private-mode browsers throw on sessionStorage: the in-memory copy still covers rebuilds.
      LoggerService.error(error);
    }
  }
}
